import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    TextField,
    useTheme,
} from '@mui/material';
import DatePicker from '../../../../components/DatePicker';
import dayjs from 'dayjs';

const AFFECTED_PAGES = ['Home', 'Services', 'About', 'Donations', 'Contact', 'Upcoming Events', 'Announcements'];

const EMPTY_FORM = { messageEn: '', messageRo: '', expirationDate: null, severity: 'Low', pages: [] };

const formToFields = (message) =>
    message
        ? {
            messageEn: message.messageEn || '',
            messageRo: message.messageRo || '',
            expirationDate: message.expirationDate ? dayjs(message.expirationDate) : null,
            severity: message.severity || 'Low',
            pages: message.pages ? message.pages.split(', ').filter(Boolean) : [],
        }
        : EMPTY_FORM;

// Add/edit dialog for an urgent message - shared by both flows so the form
// (fields, validation, styling) only needs to exist in one place.
const MessageDialog = ({ isOpen, handleClose, onSubmit, loadingSave, editingMessage }) => {
    const theme = useTheme();
    const [form, setForm] = useState(EMPTY_FORM);

    useEffect(() => {
        if (isOpen) {
            setForm(formToFields(editingMessage));
        }
    }, [isOpen, editingMessage]);

    const isEditing = Boolean(editingMessage);

    const handleTextChange = (field) => (event) => {
        let input = event.target.value;
        input = input.replace(/[^a-zA-Z0-9(),.#$!-_/ ]/g, ''); // remove all special characters except those allowed
        input = input.slice(0, 200); // limit to 200 characters
        setForm((prev) => ({ ...prev, [field]: input }));
    };

    const handleSubmit = () => {
        if (form.messageEn.trim().length === 0 || form.messageRo.trim().length === 0) {
            alert('Please enter a meaningful message in both fields');
            return;
        }
        if (!form.expirationDate || dayjs(form.expirationDate).isBefore(dayjs())) {
            alert('Please select a valid expiration date.');
            return;
        }
        if (form.pages.length === 0) {
            alert('Please select at least one page to apply this message to.');
            return;
        }

        onSubmit({
            messageEn: form.messageEn,
            messageRo: form.messageRo,
            expirationDate: form.expirationDate,
            severity: form.severity,
            pages: form.pages.join(', '),
        });
    };

    const labelStyle = {
        fontFamily: theme.typography.fontFamily,
        fontSize: '13px',
        fontWeight: 600,
        color: theme.palette.text.secondary,
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: '14px' } }}
        >
            <DialogTitle
                sx={{
                    fontFamily: theme.typography.fontFamily,
                    fontWeight: 700,
                    color: theme.palette.primary.dark,
                }}
            >
                {isEditing ? 'Edit Urgent Message' : 'Add Urgent Message'}
            </DialogTitle>
            <DialogContent>
                <DialogContentText
                    sx={{ fontFamily: theme.typography.paragraphFontFamily, fontSize: '13px', marginBottom: '16px' }}
                >
                    This message displays as a banner on the pages selected below. If it's not urgent, please post an
                    announcement in Planning Center instead.
                </DialogContentText>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <TextField
                        label="Message (English)"
                        value={form.messageEn}
                        onChange={handleTextChange('messageEn')}
                        fullWidth
                        size="small"
                    />
                    <TextField
                        label="Message (Romanian)"
                        value={form.messageRo}
                        onChange={handleTextChange('messageRo')}
                        fullWidth
                        size="small"
                    />

                    <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        <FormControl size="small" sx={{ minWidth: '160px', flex: 1 }}>
                            <InputLabel id="severity-label">Severity</InputLabel>
                            <Select
                                labelId="severity-label"
                                label="Severity"
                                value={form.severity}
                                onChange={(event) => setForm((prev) => ({ ...prev, severity: event.target.value }))}
                            >
                                <MenuItem value="Low">Low</MenuItem>
                                <MenuItem value="Medium">Medium</MenuItem>
                                <MenuItem value="High">High</MenuItem>
                            </Select>
                        </FormControl>

                        <Box sx={{ flex: 1, minWidth: '160px' }}>
                            <DatePicker
                                label="Expiration Date"
                                date={form.expirationDate}
                                handleDateChange={(date) => setForm((prev) => ({ ...prev, expirationDate: date }))}
                                minDate={dayjs().add(1, 'day')}
                            />
                        </Box>
                    </Box>

                    <FormControl size="small" fullWidth>
                        <InputLabel id="pages-label">Affected Pages</InputLabel>
                        <Select
                            labelId="pages-label"
                            multiple
                            value={form.pages}
                            onChange={(event) => {
                                const { value } = event.target;
                                setForm((prev) => ({ ...prev, pages: typeof value === 'string' ? value.split(', ') : value }));
                            }}
                            input={<OutlinedInput label="Affected Pages" />}
                            renderValue={(selected) => selected.join(', ')}
                        >
                            {AFFECTED_PAGES.map((page) => (
                                <MenuItem key={page} value={page}>
                                    <Checkbox checked={form.pages.includes(page)} size="small" />
                                    <ListItemText primary={page} primaryTypographyProps={labelStyle} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px 20px' }}>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSubmit} autoFocus variant="contained" disabled={loadingSave}>
                    {loadingSave ? <CircularProgress size={22} sx={{ color: theme.palette.background.paper }} /> : isEditing ? 'Save Changes' : 'Post'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MessageDialog;
